import { useEffect, useMemo, useState } from "react";
import { MoreOutlined, SearchOutlined } from "@ant-design/icons";
import { Dropdown, Grid, List, Modal, Spin, Typography, message } from "antd";
import type { MenuProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useDispatch, useSelector } from "react-redux";

import { authorizeUser, deleteUser, loadUsers } from "../../slice";
import { selectUsersList } from "../../selector";
import { formatUkDate } from "../../../../../utils/datetime";
import {
  DEFAULT_PAGE_SIZE,
  pageSizeOptions,
} from "../../../../../utils/pagination";
import { useTableBodyHeight } from "../../../../../utils/useTableBodyHeight";
import AddUserModal from "../AddUserModal";
import type {
  AdminUser,
  AdminUserRole,
  AdminUserStatus,
  DateRange,
} from "../../types";
import {
  DesktopTableWrap,
  EntriesFooter,
  SearchInput,
  SelectActionButton,
  ShowEntriesSelect,
  ShowEntriesWrap,
  StatusPill,
  StyledTable,
  UserCard,
  UserCardActions,
  UserCardEmail,
  UserCardMeta,
  UserCardName,
  UserCardTopRow,
  UserCellText,
  UserList,
  UserNameText,
  UsersCard,
  UsersHeader,
  UsersHeaderControls,
  UsersSubtitle,
  UsersTitle,
  UsersTitleWrap,
} from "../../style";

const { Text } = Typography;
const { useBreakpoint } = Grid;
const { Item: ListItem } = List;

const ACTION_ITEMS: MenuProps["items"] = [
  { key: "authorize", label: "Authorize" },
  // { key: "revoke", label: "R evoke" },
  { key: "delete", label: "Delete", danger: true },
];

function displayStatus(status: AdminUserStatus): string {
  if (status === "approved") return "Authorized";
  if (status === "pending") return "Pending";
  return "Revoked";
}

function displayRole(role: AdminUserRole): string {
  return role === "admin" ? "Admin" : "Receptionist";
}

function formatDate(value: string): string {
  return formatUkDate(value);
}

interface UserTableProps {
  dateRange: DateRange;
}

export default function UserTable({ dateRange }: UserTableProps) {
  const dispatch = useDispatch();
  const { data, loading } = useSelector(selectUsersList);

  const [search, setSearch] = useState("");
  const [addUserOpen, setAddUserOpen] = useState(false);
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  const [manualPageSize, setManualPageSize] = useState<number | null>(null);

  // Manual selection from the "Show entries" control; defaults to 5 everywhere.
  const desktopPageSize = manualPageSize ?? DEFAULT_PAGE_SIZE;
  const mobilePageSize = manualPageSize ?? DEFAULT_PAGE_SIZE;
  const shownPageSize = isMobile ? mobilePageSize : desktopPageSize;

  const [start, end] = dateRange ?? [null, null];
  const startDate = start ? start.format("YYYY-MM-DD") : undefined;
  const endDate = end ? end.format("YYYY-MM-DD") : undefined;

  useEffect(() => {
    dispatch(loadUsers({ startDate, endDate }));
  }, [dispatch, startDate, endDate]);

  const filtered = useMemo(() => {
    const users = data?.users ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        u.full_name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.role.toLowerCase().includes(q),
    );
  }, [data, search]);

  const [tableWrapRef, scrollY] = useTableBodyHeight(
    !isMobile,
    desktopPageSize,
    filtered.length,
    61,
  );

  function handleAuthorize(user: AdminUser) {
    dispatch(authorizeUser({ userId: user.id }));
  }

  function handleRevoke(user: AdminUser) {
    message.info(`Revoke is not available yet for ${user.full_name}`);
  }

  function confirmDelete(user: AdminUser) {
    Modal.confirm({
      title: "Delete user",
      content: `Remove ${user.full_name}? This cannot be undone.`,
      okText: "Delete",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      onOk: () => dispatch(deleteUser({ userId: user.id })),
    });
  }

  function handleAction(user: AdminUser, key: string) {
    if (key === "authorize") handleAuthorize(user);
    else if (key === "revoke") handleRevoke(user);
    else if (key === "delete") confirmDelete(user);
  }

  function renderActionDropdown(user: AdminUser) {
    return (
      <Dropdown
        menu={{
          items: ACTION_ITEMS,
          onClick: ({ key }) => handleAction(user, String(key)),
        }}
        trigger={["click"]}
      >
        <SelectActionButton>
          Select Action
          <MoreOutlined />
        </SelectActionButton>
      </Dropdown>
    );
  }

  const columns: ColumnsType<AdminUser> = [
    {
      title: "User Name",
      dataIndex: "full_name",
      key: "full_name",
      width: 180,
      ellipsis: true,
      render: (_v, row) => <UserNameText>{row.full_name}</UserNameText>,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 220,
      ellipsis: true,
      render: (_v, row) => <UserCellText>{row.email}</UserCellText>,
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 130,
      render: (_v, row) => <UserCellText>{displayRole(row.role)}</UserCellText>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (_v, row) => (
        <StatusPill $status={row.status}>
          {displayStatus(row.status)}
        </StatusPill>
      ),
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      width: 120,
      render: (_v, row) => (
        <UserCellText>{formatDate(row.created_at)}</UserCellText>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 170,
      render: (_v, row) => renderActionDropdown(row),
    },
  ];

  return (
    <UsersCard>
      <UsersHeader
        align="flex-start"
        justify="space-between"
        gap={14}
        wrap="wrap"
      >
        <UsersTitleWrap vertical gap={2}>
          <UsersTitle level={5}>Admin access control</UsersTitle>
          <UsersSubtitle>Authorize, revoke or remove access</UsersSubtitle>
        </UsersTitleWrap>

        <UsersHeaderControls align="center" gap={10}>
          <SearchInput
            placeholder="Search"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
          />
          {/* <AddUserButton type="primary" onClick={() => setAddUserOpen(true)}>
            Add User
          </AddUserButton> */}
          {isMobile && (
            <ShowEntriesWrap align="center" gap={8}>
              <Text>Show</Text>
              <ShowEntriesSelect
                value={shownPageSize}
                onChange={(v) => setManualPageSize(Number(v))}
                options={pageSizeOptions(shownPageSize)}
              />
              <Text>entries</Text>
            </ShowEntriesWrap>
          )}
        </UsersHeaderControls>
      </UsersHeader>

      {isMobile ? (
        <Spin spinning={loading}>
          <UserList<AdminUser>
            dataSource={filtered}
            rowKey={(user: AdminUser) => String(user.id)}
            pagination={{
              pageSize: mobilePageSize,
              showSizeChanger: false,
              hideOnSinglePage: true,
              itemRender: (_page, type, originalElement) => {
                if (type === "prev") return <Text>Previous</Text>;
                if (type === "next") return <Text>Next</Text>;
                return originalElement;
              },
            }}
            renderItem={(user) => (
              <ListItem key={user.id}>
                <UserCard>
                  <UserCardTopRow
                    align="center"
                    justify="space-between"
                    gap={8}
                  >
                    <UserCardName>{user.full_name}</UserCardName>
                    <StatusPill $status={user.status}>
                      {displayStatus(user.status)}
                    </StatusPill>
                  </UserCardTopRow>
                  <UserCardEmail>{user.email}</UserCardEmail>
                  <UserCardMeta>
                    {displayRole(user.role)} · {formatDate(user.created_at)}
                  </UserCardMeta>
                  <UserCardActions align="center">
                    {renderActionDropdown(user)}
                  </UserCardActions>
                </UserCard>
              </ListItem>
            )}
          />
        </Spin>
      ) : (
        <DesktopTableWrap ref={tableWrapRef}>
          <Spin spinning={loading}>
            <StyledTable
              columns={columns as ColumnsType<unknown>}
              dataSource={filtered}
              rowKey={(row) => String((row as AdminUser).id)}
              tableLayout="fixed"
              scroll={{ x: 950, y: scrollY }}
              pagination={{
                pageSize: desktopPageSize,
                showSizeChanger: false,
                hideOnSinglePage: true,
                position: ["bottomCenter"],
                itemRender: (_page, type, originalElement) => {
                  if (type === "prev") return <Text>Previous</Text>;
                  if (type === "next") return <Text>Next</Text>;
                  return originalElement;
                },
              }}
            />
          </Spin>
          <EntriesFooter align="center" gap={8}>
            <Text>Show</Text>
            <ShowEntriesSelect
              value={shownPageSize}
              onChange={(v) => setManualPageSize(Number(v))}
              options={pageSizeOptions(shownPageSize)}
            />
            <Text>entries</Text>
          </EntriesFooter>
        </DesktopTableWrap>
      )}

      <AddUserModal open={addUserOpen} onClose={() => setAddUserOpen(false)} />
    </UsersCard>
  );
}
